import type { ReactNode } from 'react';
import { Box, Button, Container, ColumnLayout, ExpandableSection, Header, Link, SpaceBetween } from '@cloudscape-design/components';
import { PageLayout } from '@deloitte-us-consulting/gp-sdk';
import { useNavigate } from 'react-router';

const TEMPLATE_URL = 'https://github.com/Deloitte-US-Consulting/gp-app-template';

interface Step {
  title: string;
  body: ReactNode;
}

/**
 * Landing page for prospective app builders — orientation, not the source of
 * truth. The canonical, detailed flow lives in docs/building-an-app.md and the
 * build-gp-app skill; this page summarizes it in plain language and links out
 * rather than duplicating it, so there's exactly one place that can go stale.
 */
export default function OnboardApp() {
  const navigate = useNavigate();

  const goToSubmit = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    navigate('/onboard/submit');
  };
  const goToMine = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    navigate('/onboard/mine');
  };

  const STEPS: Step[] = [
    {
      title: '1. Get your own copy of the starter app',
      body: (
        <>
          Start from our{' '}
          <Link external href={TEMPLATE_URL}>
            gp-app-template
          </Link>{' '}
          — a ready-made project that already knows how to talk to the platform. Pick a short name for your app
          (lowercase, no spaces — like "pursuits-tracker") when you create it; you'll use that same name when you
          submit below.
        </>
      ),
    },
    {
      title: '2. Answer a few quick questions',
      body: 'A setup script asks what your app is called, what it does, and whether you want the AI assistant turned on to start. That’s it — nothing here needs AWS access or anyone’s approval.',
    },
    {
      title: '3. Try it on your own computer',
      body: 'One command runs your app locally so you can see and build your screen before anyone else does.',
    },
    {
      title: '4. Add the AI assistant, any time',
      body: 'Want a Claude-powered assistant in your app? It’s one line to turn on — already governed by the platform’s usage and data rules, so you never need your own API key.',
    },
    {
      title: '5. Submit your app here for review',
      body: (
        <>
          Once it runs on your computer and you know your app’s name, repo link, and who owns it,{' '}
          <Link href="/onboard/submit" onFollow={goToSubmit}>
            submit it for review
          </Link>
          . A platform admin looks it over and gets the remaining setup started.
        </>
      ),
    },
    {
      title: '6. Go live',
      body: (
        <>
          Once the platform team finishes wiring things up on their end, your app is switched on and shows up for
          everyone you said should see it. Check{' '}
          <Link href="/onboard/mine" onFollow={goToMine}>
            My submissions
          </Link>{' '}
          any time to see where things stand.
        </>
      ),
    },
  ];

  return (
    <PageLayout
      title="Onboard a New Application"
      description="Build a screen for the GP Platform and get login, navigation, notifications, and a governed AI assistant built in."
      actions={
        <Button variant="primary" onClick={() => navigate('/onboard/submit')}>
          Submit your app
        </Button>
      }
    >
      <SpaceBetween size="l">
        <Container header={<Header variant="h2">How it works</Header>}>
          <ColumnLayout columns={2} variant="text-grid">
            {STEPS.map((step) => (
              <div key={step.title}>
                <Box variant="h4">{step.title}</Box>
                <Box color="text-body-secondary">{step.body}</Box>
              </div>
            ))}
          </ColumnLayout>
        </Container>

        <ExpandableSection headerText="Want more detail, or building with Claude's help?">
          <SpaceBetween size="s">
            <Box>
              Start from{' '}
              <Link external href={TEMPLATE_URL}>
                gp-app-template
              </Link>{' '}
              on GitHub, or just ask Claude to help you build an app "for the GP Platform" — it knows the same steps
              as this page and can walk you through them.
            </Box>
            <Box>
              Already submitted something?{' '}
              <Link href="/onboard/mine" onFollow={goToMine}>
                Check My submissions
              </Link>{' '}
              for its status.
            </Box>
          </SpaceBetween>
        </ExpandableSection>

        <ExpandableSection headerText="What happens after I submit?">
          <Box color="text-body-secondary">
            A platform admin reviews what you sent — your repo, point of contact, description, and who it's for
            — and either approves it or lets you know why not. Approval kicks off the platform team's remaining
            setup work behind the scenes; once that's done, your app goes live. You can check progress any time from{' '}
            <Link href="/onboard/mine" onFollow={goToMine}>
              My submissions
            </Link>
            .
          </Box>
        </ExpandableSection>
      </SpaceBetween>
    </PageLayout>
  );
}
